import React, { useState } from 'react';
import StylingMockLayout from '../shared/StylingMockLayout';
import FloatingElements from 'components/animation/FloatingElements';
import ProgressIndicator from 'components/animation/ProgressIndicator';
import LoadingButton from 'components/button/LoadingButton';
import './StylingMockAnimations.css';

/**
 * Styling Mock Animations Page
 * Showcases animation components
 */
const StylingMockAnimations = () => {
  
  // ProgressIndicator state
  const [progressValue, setProgressValue] = useState(75);
  const [progressDuration, setProgressDuration] = useState(5000);
  const [progressKey, setProgressKey] = useState(0); // Key to force re-render

  const handleProgressChange = () => {
    // Force re-render by changing key
    setProgressKey(prev => prev + 1);
  };

  return (
    <StylingMockLayout>
      <div className="styling-mock-animations">
        <div className="styling-mock-section">
          <h2 className="styling-mock-section-title">Animations</h2>
          <p className="styling-mock-section-description">
            Edit the values in real-time to see how animation components respond. All changes are reflected immediately in the demo.
          </p>
        </div>

        {/* FloatingElements */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">FloatingElements</h3>
          
          {/* Colorful FloatingElements */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-animation-example">
                <h4>FloatingElements - Colorful</h4>
                <FloatingElements elementCount={5} monochrome={false} />
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<FloatingElements elementCount={5} monochrome={false} />`}
            </div>
          </div>

          {/* Monochrome FloatingElements */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-animation-example">
                <h4>FloatingElements - Monochrome</h4>
                <FloatingElements elementCount={5} monochrome={true} />
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<FloatingElements elementCount={5} monochrome={true} />`}
            </div>
          </div>
        </div>

        {/* ProgressIndicator */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">ProgressIndicator</h3>
          
          {/* Interactive Controls */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-interactive-controls">
                <div className="styling-mock-control-group">
                  <label>Progress Value (0-100):</label>
                  <div className="styling-mock-range-input-group">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={progressValue}
                      onChange={(e) => {
                        setProgressValue(parseInt(e.target.value));
                        handleProgressChange();
                      }}
                      className="styling-mock-range-input"
                    />
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={progressValue}
                      onChange={(e) => {
                        const val = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                        setProgressValue(val);
                        handleProgressChange();
                      }}
                      className="styling-mock-number-input"
                    />
                  </div>
                </div>
                <div className="styling-mock-control-group">
                  <label>Animation Duration (ms):</label>
                  <input
                    type="number"
                    min="100"
                    max="30000"
                    step="100"
                    value={progressDuration}
                    onChange={(e) => {
                      setProgressDuration(parseInt(e.target.value) || 1000);
                      handleProgressChange();
                    }}
                    className="styling-mock-input"
                  />
                </div>
                <div className="styling-mock-control-group">
                  <button
                    onClick={handleProgressChange}
                    className="styling-mock-button"
                  >
                    Restart Animation
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Colorful ProgressIndicator */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-animation-example">
                <h4>ProgressIndicator - Colorful ({progressValue}%)</h4>
                <div className="progress-indicator-container">
                  <ProgressIndicator 
                    key={`colorful-${progressKey}`}
                    progress={progressValue} 
                    duration={progressDuration} 
                    monochrome={false} 
                  />
                </div>
                <p className="progress-indicator-label">{progressValue}% Complete</p>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<ProgressIndicator progress={${progressValue}} duration={${progressDuration}} monochrome={false} />`}
            </div>
          </div>

          {/* Monochrome ProgressIndicator */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-animation-example">
                <h4>ProgressIndicator - Monochrome ({progressValue}%)</h4>
                <div className="progress-indicator-container">
                  <ProgressIndicator 
                    key={`monochrome-${progressKey}`}
                    progress={progressValue} 
                    duration={progressDuration} 
                    monochrome={true} 
                  />
                </div>
                <p className="progress-indicator-label">{progressValue}% Complete</p>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<ProgressIndicator progress={${progressValue}} duration={${progressDuration}} monochrome={true} />`}
            </div>
          </div>
        </div>

        {/* Loading Animations */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Loading Animations</h3>
          
          {/* Colorful Loading Buttons */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-animation-example">
                <h4>Button Loading States - Colorful</h4>
                <LoadingButton variant="primary" size="small" monochrome={false} />
                <LoadingButton variant="primary" size="medium" monochrome={false} />
                <LoadingButton variant="primary" size="large" monochrome={false} />
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<LoadingButton variant="primary" size="small" monochrome={false} />
<LoadingButton variant="primary" size="medium" monochrome={false} />
<LoadingButton variant="primary" size="large" monochrome={false} />`}
            </div>
          </div>

          {/* Monochrome Loading Buttons */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-animation-example">
                <h4>Button Loading States - Monochrome</h4>
                <LoadingButton variant="primary" size="small" monochrome={true} />
                <LoadingButton variant="primary" size="medium" monochrome={true} />
                <LoadingButton variant="primary" size="large" monochrome={true} />
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<LoadingButton variant="primary" size="small" monochrome={true} />
<LoadingButton variant="primary" size="medium" monochrome={true} />
<LoadingButton variant="primary" size="large" monochrome={true} />`}
            </div>
          </div>
        </div>
      </div>
    </StylingMockLayout>
  );
};

export default StylingMockAnimations;

